import type { ReactNode } from 'react';
import { CircleCheck, CircleDashed, OctagonAlert, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ProcessSnapshot, StageTotal, ThreadSnapshot } from './profiler.types';
import { TICK_BUDGET_MS, budgetShare, type Health, type Light } from './profiler.health';
import { fmtBytes, fmtClock, fmtMs, fmtNumber, fmtShare, stageHelp, stageLabel, threadLabel } from './profiler.format';
import s from './Profiler.module.scss';

// ============================================================
// Profileur : petits composants partagés par la liste et la page d'un serveur
// ============================================================
// Règles tenues partout : un état ne se lit jamais à la seule couleur (une forme et un libellé
// l'accompagnent), et un texte ne porte jamais la couleur d'une donnée (c'est la marque à côté
// qui la porte).
// ============================================================

export const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

const LIGHT_ICON: Record<Light, LucideIcon> = { ok: CircleCheck, watch: TriangleAlert, bad: OctagonAlert, none: CircleDashed };
const LIGHT_CLASS: Record<Light, string> = { ok: s.lightOk, watch: s.lightWatch, bad: s.lightBad, none: s.lightNone };

/** Voyant : une forme, une couleur et deux ou trois mots. `reason` s'affiche dessous quand il est demandé. */
export function StatusLight({ health, showReason = false, size = 'md' }: { health: Health; showReason?: boolean; size?: 'md' | 'lg' }) {
  const Icon = LIGHT_ICON[health.light];
  return (
    <span className={cx(s.light, LIGHT_CLASS[health.light], size === 'lg' && s.lightLg)}>
      <span className={s.lightIcon} aria-hidden><Icon size={size === 'lg' ? 22 : 16} strokeWidth={2.25} /></span>
      <span className={s.lightText}>
        <span className={s.lightTitle}>{health.title}</span>
        {showReason && <span className={s.lightReason}>{health.level ? `Monde ${health.level} : ` : ''}{health.reason}</span>}
      </span>
    </span>
  );
}

/**
 * Durée moyenne d'un tick face au budget de 50 ms : la partie pleine est le temps pris, la piste le
 * temps disponible. Le plus long tick est un repère sur la même échelle ; au-delà du budget il est
 * calé au bout, et sa valeur est écrite à côté.
 */
export function BudgetMeter({ averageMs, maxMs, light }: { averageMs: number; maxMs: number | null; light: Light }) {
  const fill = budgetShare(averageMs) * 100;
  const mark = maxMs == null ? null : budgetShare(maxMs) * 100;
  const label = `Durée moyenne ${fmtMs(averageMs)} sur ${TICK_BUDGET_MS} ms disponibles`
    + (maxMs == null ? '' : `, plus long tick ${fmtMs(maxMs)}`);
  return (
    <span className={cx(s.meter, LIGHT_CLASS[light])} role="img" aria-label={label} title={label}>
      <span className={s.meterTrack}>
        <span className={s.meterFill} style={{ width: `${Math.max(fill, averageMs > 0 ? 2 : 0)}%` }} />
        {mark != null && <span className={s.meterMark} style={{ left: `${mark}%` }} />}
      </span>
    </span>
  );
}

/** Chiffre de tête : un libellé, une valeur, et la phrase qui dit sur quoi elle porte. */
export function StatTile({ label, value, hint, children }: { label: string; value: ReactNode; hint?: ReactNode; children?: ReactNode }) {
  return (
    <div className={s.tile}>
      <span className={s.tileLabel}>{label}</span>
      <span className={s.tileValue}>{value}</span>
      {children}
      {hint && <span className={s.tileHint}>{hint}</span>}
    </div>
  );
}

/** Encadré d'explication ou d'avertissement, avec son pictogramme. */
export function Note({ tone = 'info', icon: Icon, title, children }: {
  tone?: 'info' | 'warn' | 'bad';
  icon: LucideIcon;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx(s.note, tone === 'warn' && s.noteWarn, tone === 'bad' && s.noteBad)} role="note">
      <span className={s.noteIcon} aria-hidden><Icon size={16} /></span>
      <div className={s.noteBody}>
        {title && <strong className={s.noteTitle}>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- répartition par étape

/** Une ligne : nom en clair, barre à l'échelle de la durée du tick, durée par tick et part. */
function StageRow({ stage, sub }: { stage: StageTotal; sub?: boolean }) {
  const help = stageHelp(stage.name);
  return (
    <li className={cx(s.barRow, sub && s.barRowSub)} title={help ?? undefined}>
      <span className={s.barLabel}>
        <span className={s.barName}>{stageLabel(stage.name)}</span>
        {!sub && help && <span className={s.barHelp}>{help}</span>}
      </span>
      <span className={s.barTrack} aria-hidden>
        <span className={s.barFill} style={{ width: `${Math.min(100, Math.max(stage.share * 100, stage.share > 0 ? 0.5 : 0))}%` }} />
      </span>
      <span className={s.barValue}>
        <strong>{fmtShare(stage.share)}</strong>
        <span className={s.barDetail}>{fmtMs(stage.msPerTick)} par tick{stage.bytesPerTick != null && stage.bytesPerTick >= 1024 ? ` · ${fmtBytes(stage.bytesPerTick)}` : ''}</span>
      </span>
    </li>
  );
}

/**
 * Où part le temps d'un tick : une ligne par étape, la plus lourde d'abord, ses sous-étapes dessous.
 * Une seule teinte : les étapes sont nommées par leur texte, la longueur de la barre dit le reste.
 * Ce qui n'entre dans aucune étape gardée par la courbe est réuni sous « Autres étapes ».
 */
export function StageBars({ stages, subStages, averageMs }: { stages: StageTotal[]; subStages: StageTotal[]; averageMs: number | null }) {
  if (stages.length === 0) return <p className={s.empty}>Aucune mesure d'étape sur cette période.</p>;
  const listed = stages.reduce((sum, st) => sum + st.share, 0);
  const rest = Math.max(0, 1 - listed);
  return (
    <ul className={s.bars}>
      {stages.map((stage) => (
        <li key={stage.name} className={s.barGroup}>
          <ul className={s.barGroupList}>
            <StageRow stage={stage} />
            {subStages.filter((sub) => sub.parent === stage.name && sub.share >= 0.005).map((sub) => <StageRow key={sub.name} stage={sub} sub />)}
          </ul>
        </li>
      ))}
      {rest >= 0.005 && (
        <li className={s.barGroup}>
          <ul className={s.barGroupList}>
            <StageRow stage={{ name: 'Autres étapes', parent: null, totalMs: 0, share: rest, msPerTick: (averageMs ?? 0) * rest, allocatedBytes: 0, bytesPerTick: null }} />
          </ul>
        </li>
      )}
    </ul>
  );
}

// ---------------------------------------------------------------- fils

/** Fils les plus gourmands du dernier relevé : 100 % = un cœur du processeur occupé en continu. */
export function ThreadBars({ process }: { process: ProcessSnapshot | null }) {
  if (!process || !process.hasThreads) {
    return <p className={s.empty}>Pas encore de relevé des fils sur ce serveur (il en faut deux, à dix secondes d'écart).</p>;
  }
  const threads: ThreadSnapshot[] = process.threads.filter((t) => t.cpuPercent >= 0.5).slice(0, 8);
  return (
    <>
      <p className={s.cardLead}>
        Le serveur occupe <strong>{fmtNumber(process.cpuCores, 1)} cœur{process.cpuCores >= 2 ? 's' : ''}</strong> du processeur,
        dont {process.tickThreadsCpuCores > 0 && process.tickThreadsCpuCores < 0.05 ? 'moins de 0,1' : fmtNumber(process.tickThreadsCpuCores, 1)} pour
        faire avancer les mondes, répartis sur {fmtNumber(process.threadCount)} fils.
        {process.sampledAtUtc != null && ` Relevé de ${fmtClock(process.sampledAtUtc)}, sur ${fmtNumber(process.sampleSeconds)} s.`}
      </p>
      {threads.length === 0 ? <p className={s.empty}>Aucun fil au-dessus de 0,5 % d'un cœur.</p> : (
        <ul className={s.bars}>
          {threads.map((t) => (
            <li key={`${t.threadId}:${t.name}`} className={s.barRow} title={`${t.name} (fil n° ${t.threadId})`}>
              <span className={s.barLabel}>
                <span className={s.barName}>{threadLabel(t.name)}</span>
                {t.isTickThread && <span className={s.tag}>fait avancer un monde</span>}
              </span>
              <span className={s.barTrack} aria-hidden>
                <span className={s.barFill} style={{ width: `${Math.min(100, Math.max(t.cpuPercent, 0.5))}%` }} />
              </span>
              <span className={s.barValue}>
                <strong>{fmtNumber(t.cpuPercent, 0)} %</strong>
                <span className={s.barDetail}>d'un cœur</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
